import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/lib/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Pricing from "@/pages/Pricing";
import SectorPage from "@/pages/SectorPage";
import AppLayout from "@/pages/AppLayout";
import Workforce from "@/pages/Workforce";
import HiredWorkers from "@/pages/HiredWorkers";
import WorkerPage from "@/pages/WorkerPage";
import AppHome from "@/pages/AppHome";
import Settings from "@/pages/Settings";
import PlatformConsole from "@/pages/PlatformConsole";
import SkillMarketplace from "@/pages/SkillMarketplace";
import Documents from "@/pages/Documents";
import Email from "@/pages/Email";
import Meetings from "@/pages/Meetings";
import Knowledge from "@/pages/Knowledge";
import SkillStudio from "@/pages/SkillStudio";
import SkillBuilder from "@/pages/SkillBuilder";
import SkillsMaintenance from "@/pages/SkillsMaintenance";
import OrchestrationsList from "@/pages/OrchestrationsList";
import OrchestrationBuilder from "@/pages/OrchestrationBuilder";
import OrchestrationRuns from "@/pages/OrchestrationRuns";
import OrchestrationRunDetail from "@/pages/OrchestrationRunDetail";
import Legal from "@/pages/Legal";
import PublicLegalPage from "@/pages/PublicLegalPage";
import LegalEnterpriseInquiry from "@/pages/LegalEnterpriseInquiry";
import LegalDept from "@/pages/LegalDept";
import LegalMattersList from "@/pages/LegalMattersList";
import LegalNewMatter from "@/pages/LegalNewMatter";
import LegalMatterWorkspace from "@/pages/LegalMatterWorkspace";
import LegalAnalysisList from "@/pages/LegalAnalysisList";
import LegalAnalysisNew from "@/pages/LegalAnalysisNew";
import LegalAnalysisRunDetail from "@/pages/LegalAnalysisRunDetail";
import AttorneyDeclarationForm from "@/pages/AttorneyDeclarationForm";
import Billing from "@/pages/Billing";
import IntegrationsPublic from "@/pages/IntegrationsPublic";
import PlatformNotice from "@/pages/PlatformNotice";
import PaywallModal from "@/components/PaywallModal";

function Protected({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#fafaf7] text-gray-500 text-sm">
                Loading…
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/terms" element={<PublicLegalPage doc="terms" />} />
                    <Route path="/privacy" element={<PublicLegalPage doc="privacy" />} />
                    <Route path="/sectors/:slug" element={<SectorPage />} />
                    <Route path="/legal" element={<Legal />} />
                    <Route path="/legal/:doc" element={<Legal />} />
                    <Route path="/legal-enterprise-inquiry" element={<LegalEnterpriseInquiry />} />
                    <Route path="/legal/platform-notice" element={<PlatformNotice />} />
                    <Route path="/integrations" element={<IntegrationsPublic />} />
                    <Route
                        path="/app"
                        element={
                            <Protected>
                                <AppLayout />
                            </Protected>
                        }
                    >
                        <Route index element={<AppHome />} />
                        <Route path="workforce" element={<Workforce />} />
                        <Route path="workforce/hired" element={<HiredWorkers />} />
                        <Route path="workers/:hiredId" element={<WorkerPage />} />
                        <Route path="skill-marketplace" element={<SkillsMaintenance />} />
                        <Route path="documents" element={<Documents />} />
                        <Route path="email" element={<Email />} />
                        <Route path="meetings" element={<Meetings />} />
                        <Route path="knowledge" element={<Knowledge />} />
                        <Route path="skills" element={<SkillStudio />} />
                        <Route path="skills/new" element={<SkillBuilder />} />
                        <Route path="skills/:skillId" element={<SkillBuilder />} />
                        <Route path="orchestrations" element={<OrchestrationsList />} />
                        <Route path="orchestrations/new" element={<OrchestrationBuilder />} />
                        <Route path="orchestrations/:orchId/edit" element={<OrchestrationBuilder />} />
                        <Route path="orchestrations/:orchId/runs" element={<OrchestrationRuns />} />
                        <Route path="orchestrations/:orchId/runs/:runId" element={<OrchestrationRunDetail />} />
                        <Route path="legal" element={<LegalDept />} />
                        <Route path="legal/matters" element={<LegalMattersList />} />
                        <Route path="legal/matters/new" element={<LegalNewMatter />} />
                        <Route path="legal/matters/:matterId" element={<LegalMatterWorkspace />} />
                        <Route path="legal/analysis" element={<LegalAnalysisList />} />
                        <Route path="legal/analysis/attorney-declaration" element={<AttorneyDeclarationForm />} />
                        <Route path="legal/analysis/new" element={<LegalAnalysisNew />} />
                        <Route path="legal/analysis/:orchId/runs/:runId" element={<LegalAnalysisRunDetail />} />
                        <Route path="billing" element={<Billing />} />
                        <Route path="settings" element={<Settings />} />
                        <Route path="platform" element={<PlatformConsole />} />
                    </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <PaywallModal />
                <Toaster position="top-right" />
            </BrowserRouter>
        </AuthProvider>
    );
}
